/**
 * 조직 관련 API 훅
 */
import { useState, useEffect, useCallback } from 'react';
import {
  organizationApi,
  getErrorMessage,
  ApiClientError,
  type Organization,
  type OrganizationSummary,
  type Store,
} from '@/lib/api';

interface UseOrganizationsState {
  organizations: Organization[];
  loading: boolean;
  error: Error | null;
}

interface UseOrganizationsReturn extends UseOrganizationsState {
  refetch: () => Promise<void>;
}

/**
 * 조직 목록 조회 훅
 */
export function useOrganizations(): UseOrganizationsReturn {
  const [state, setState] = useState<UseOrganizationsState>({
    organizations: [],
    loading: true,
    error: null,
  });

  const fetchOrganizations = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await organizationApi.getOrganizations();
      setState({
        organizations: response.data,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message = getErrorMessage(err, '조직 목록을 불러오는데 실패했습니다');
      setState((prev) => ({
        ...prev,
        loading: false,
        error: new Error(message),
      }));
    }
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return {
    ...state,
    refetch: fetchOrganizations,
  };
}

interface UseOrgStoresState {
  organization: OrganizationSummary | null;
  stores: Store[];
  loading: boolean;
  error: Error | null;
}

interface UseOrgStoresReturn extends UseOrgStoresState {
  refetch: () => Promise<void>;
}

interface UseOrgStoresOptions {
  pickupAvailable?: boolean;
}

/**
 * 조직별 매장 목록 조회 훅
 * @param orgParam 조직 슬러그, 닉네임(@prefix), 또는 ID
 * @param options 필터 옵션
 */
export function useOrgStores(
  orgParam: string | undefined,
  options: UseOrgStoresOptions = {}
): UseOrgStoresReturn {
  // 의존성 안정화를 위해 개별 값으로 추출
  const pickupAvailable = options.pickupAvailable;

  const [state, setState] = useState<UseOrgStoresState>({
    organization: null,
    stores: [],
    loading: true,
    error: null,
  });

  const fetchStores = useCallback(async () => {
    if (!orgParam) {
      setState({
        organization: null,
        stores: [],
        loading: false,
        error: null,
      });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await organizationApi.getStoresByOrg(orgParam, {
        pickup_available: pickupAvailable,
      });
      setState({
        organization: response.data.organization ?? null,
        stores: response.data.stores,
        loading: false,
        error: null,
      });
    } catch (err) {
      const isNotFound = err instanceof ApiClientError && err.status === 404;
      const message = isNotFound
        ? '조직을 찾을 수 없습니다'
        : getErrorMessage(err, '매장 목록을 불러오는데 실패했습니다');
      setState((prev) => ({
        ...prev,
        loading: false,
        error: new Error(message),
      }));
    }
  }, [orgParam, pickupAvailable]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  return {
    ...state,
    refetch: fetchStores,
  };
}
